import { type Dispatch, type SetStateAction } from "react";

import { EnrichmentStafferCallItem } from "./EnrichmentStafferCallItem";
import { Column, ErrorText, LoadingIndicator, Text } from "@/components";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useGetCalls } from "@/hooks/queries/useGetCalls";
import type { EnrichmentCandidacy } from "./types";

export const StafferCallsPreviewDialog = ({
  candidacy,
  onOpenChange,
  open,
  selectedStafferCallIds,
  setSelectedStafferCallIds,
}: {
  candidacy: EnrichmentCandidacy;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  selectedStafferCallIds: Set<string>;
  setSelectedStafferCallIds: Dispatch<SetStateAction<Set<string>>>;
}) => {
  /* Database CRUD Operations */
  // Read
  const callsQuery = useGetCalls({
    talentId: candidacy.talentId,
  });

  /* Handlers */
  const handleToggleCallId = (callId: string) => {
    setSelectedStafferCallIds((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId);
      else next.add(callId);
      return next;
    });
  };

  /* Render */
  const renderBody = () => {
    if (callsQuery.isPending) return <LoadingIndicator variant="medium" />;
    if (callsQuery.isError) {
      return <ErrorText errorMessage="Error retrieving calls." />;
    }
    if (callsQuery.data.length === 0) {
      return (
        <Text variant="muted">
          No staffer calls recorded for this candidate.
        </Text>
      );
    }

    return (
      <Column className="gap-2">
        {callsQuery.data.map((call) => (
          <EnrichmentStafferCallItem
            key={call.id}
            call={call}
            candidacy={candidacy}
            isSelected={selectedStafferCallIds.has(call.id)}
            onToggle={() => {
              handleToggleCallId(call.id);
            }}
          />
        ))}
      </Column>
    );
  };

  const render = () => {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Staffer calls</DialogTitle>
          </DialogHeader>
          {renderBody()}
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
